import React, { useContext, useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import styled from 'styled-components';

import { countryOptions, professionOptions, shareStudyViaOptions, utils } from '../../../core';
import CheckboxInput from '../../ui/CheckboxInput';
import Form from '../../ui/Form';
import PhoneNumberInput from '../../ui/PhoneNumberInput';
import SelectInput from '../../ui/SelectInput';
import TextInput from '../../ui/TextInput';
import FrontdeskPractitionerFormContext from './FrontdeskPractitionerFormContext';
import { renderDefaultFrontdeskPractitionerInputValues } from './helper';

const CheckboxRow = styled.div`
  margin: 20px 0;
`;

const FrontdeskPractitionerInputs = () => {
  const { setValue, getValues } = useFormContext();
  const { selectedPractitioner, editing, mode, showDifferentLocationWarning } = useContext(
    FrontdeskPractitionerFormContext
  );
  const isSelectedPractitioner = selectedPractitioner && editing;

  useEffect(() => {
    if (selectedPractitioner && !Array.isArray(selectedPractitioner)) {
      const practitioner = renderDefaultFrontdeskPractitionerInputValues(selectedPractitioner);

      if (practitioner && editing) {
        for (const [fieldName, value] of Object.entries(practitioner)) {
          setValue(fieldName, value);
        }
      }
    }
  }, [selectedPractitioner, editing, setValue]);

  return (
    <React.Fragment>
      <Form.RowLabel>Practitioner Personal Information</Form.RowLabel>
      <Form.Row>
        <Form.Controller
          as={TextInput}
          type="text"
          name={`salutation`}
          label="Salutation"
          placeholder={!isSelectedPractitioner ? 'Salutation' : ''}
          rules={utils.validate('salutation')}
          disabled={isSelectedPractitioner || false}
        />

        <Form.Controller
          as={TextInput}
          type="text"
          name={`firstName`}
          label="First Name"
          placeholder={!isSelectedPractitioner ? 'First Name' : ''}
          rules={utils.validate('firstName', 'required')}
          disabled={isSelectedPractitioner || false}
        />

        <Form.Controller
          as={TextInput}
          type="text"
          name={`lastName`}
          label="Last Name"
          placeholder={!isSelectedPractitioner ? 'Last Name' : ''}
          rules={utils.validate('lastName', 'required')}
          disabled={isSelectedPractitioner || false}
        />

        <Form.Controller
          as={SelectInput}
          name="profession"
          options={professionOptions}
          label="Profession"
          placeholder={!isSelectedPractitioner ? 'Select Profession' : ''}
          rules={utils.validate('profession', 'required')}
          disabled={isSelectedPractitioner || false}
        />

        <Form.Controller
          as={TextInput}
          type="text"
          name={`title`}
          label="Title"
          placeholder={!isSelectedPractitioner ? 'Title' : ''}
          disabled={isSelectedPractitioner || false}
        />
      </Form.Row>

      <Form.RowLabel>Practitioner Primary Contact Information</Form.RowLabel>
      <Form.Row noMargin>
        <Form.Controller
          as={TextInput}
          type="text"
          name={`practiceName`}
          label="Practice Name"
          placeholder={!isSelectedPractitioner ? 'Practice Name' : ''}
          rules={utils.validate('practiceName', 'required')}
          disabled={isSelectedPractitioner || false}
        />

        <Form.Controller
          as={TextInput}
          type="text"
          name={`locationName`}
          label="Location Name"
          placeholder={!isSelectedPractitioner ? 'Location Name' : ''}
          rules={utils.validate('locationName', 'required')}
          disabled={isSelectedPractitioner || false}
          errorMessageOnDisable={
            showDifferentLocationWarning
              ? 'This location is different from the referring practitioner'
              : ''
          }
        />

        <Form.Controller
          as={TextInput}
          type="text"
          name={`address`}
          label="Address 1"
          placeholder={!isSelectedPractitioner ? 'Address 1' : ''}
          rules={utils.validate('address', 'required')}
          disabled={isSelectedPractitioner || false}
        />

        <Form.Controller
          as={TextInput}
          type="text"
          name={`address2`}
          label="Address 2"
          placeholder={!isSelectedPractitioner ? 'Address 2' : ''}
          rules={utils.validate('address2')}
          disabled={isSelectedPractitioner || false}
        />

        <Form.Controller
          as={TextInput}
          type="text"
          name={`emailAddress`}
          label="Email address"
          placeholder={!isSelectedPractitioner ? 'Email address' : ''}
          rules={utils.validate('emailAddress')}
          disabled={isSelectedPractitioner || false}
        />

        <Form.Controller
          as={PhoneNumberInput}
          type="tel"
          name={`phoneNumber`}
          label="Phone Number"
          placeholder={!isSelectedPractitioner ? 'Phone Number' : ''}
          rules={utils.validate('phoneNumber')}
          disabled={isSelectedPractitioner || false}
        />

        <Form.Controller
          as={PhoneNumberInput}
          type="tel"
          name={`faxNumber`}
          label="Fax Number"
          placeholder={!isSelectedPractitioner ? 'Fax Number' : ''}
          rules={utils.validate('faxNumber')}
          disabled={isSelectedPractitioner || false}
        />

        <Form.Controller
          as={SelectInput}
          options={countryOptions}
          name={`country`}
          label="Country"
          rules={utils.validate('country', 'required')}
          disabled={isSelectedPractitioner || false}
        />

        <Form.Controller
          as={TextInput}
          type="text"
          name={`state`}
          label="State/Province"
          placeholder={!isSelectedPractitioner ? 'State/Province' : ''}
          rules={utils.validate('state', 'required')}
          disabled={isSelectedPractitioner || false}
        />

        <Form.Controller
          as={TextInput}
          type="text"
          name={`city`}
          label="City"
          placeholder={!isSelectedPractitioner ? 'City' : ''}
          rules={utils.validate('city', 'required')}
          disabled={isSelectedPractitioner || false}
        />

        <Form.Controller
          as={TextInput}
          type="text"
          name={`postalCode`}
          label="Postal/Zip Code"
          placeholder={!isSelectedPractitioner ? 'Postal/Zip Code' : ''}
          rules={utils.validate('postalCode', 'required')}
          disabled={isSelectedPractitioner || false}
        />

        <Form.Controller
          as={SelectInput}
          isTag
          name="sharedMethod"
          options={shareStudyViaOptions}
          label="Share Study Via"
          rules={utils.validate('sharedMethod')}
          disabled={isSelectedPractitioner || false}
        />
      </Form.Row>

      {editing && mode === 'additional' && (
        <CheckboxRow>
          <Form.Controller
            name={`isPrimary`}
            isCheckbox
            render={(props: any) => (
              <CheckboxInput
                onChange={(e) => {
                  setValue('isPrimary', e.target.checked);
                }}
                checked={getValues('isPrimary') ? !!getValues('isPrimary') : false}
                name={props.name}
                id={props.name}
                label="Mark as Primary Health Practitioner"
              />
            )}
          />
        </CheckboxRow>
      )}
    </React.Fragment>
  );
};

export default FrontdeskPractitionerInputs;
